import type { ErrorLike } from '@apollo/client';
import { getFirstNestJsGraphQLError } from '@pcg/graphql-kit';
import { useToast } from '@nuxt/ui/composables/useToast';
import { storeToRefs } from 'pinia';
import { readonly } from 'vue';

import { useErrorStore } from '../stores/error';
import type { ShowErrorOptions } from '../stores/types';

interface ErrorConfig {
  title: string;
  description: string;
}

const INTERNAL_ERROR: ErrorConfig = {
  title: 'Internal error',
  description: 'Something went wrong. Please try again later.',
};

const ERROR_CONFIG: Record<number, ErrorConfig> = {
  401: {
    title: 'Unauthorized',
    description: 'Please sign in to continue.',
  },
  403: {
    title: 'Access denied',
    description: 'You do not have permission to access this resource.',
  },
  404: {
    title: 'Not found',
    description: 'The page could not be found.',
  },
  500: INTERNAL_ERROR,
};

/**
 * Status codes that should trigger ErrorView display (full page errors).
 */
const ERROR_VIEW_STATUS_CODES = [401, 403, 404, 500];

/**
 * Get error config for a status code, falling back to default.
 */
const getErrorConfig = (statusCode: number): ErrorConfig => {
  return ERROR_CONFIG[statusCode] ?? INTERNAL_ERROR;
};

/**
 * Check if the error status code should be shown as a full-page ErrorView.
 */
const isErrorViewStatusCode = (statusCode: number): boolean => {
  return ERROR_VIEW_STATUS_CODES.includes(statusCode);
};

/**
 * Composable for error handling with i18n support.
 *
 * @example
 * ```typescript
 * const { error, showError, clearError, handleServerError, handleServerErrorWithToast } = useError();
 *
 * // Show a 404 error with default messages
 * showError({ statusCode: 404 });
 *
 * // Show a custom error
 * showError({
 *   statusCode: 500,
 *   title: 'Custom Error',
 *   description: 'Something went wrong',
 * });
 *
 * // Handle Apollo errors - shows ErrorView for 401/403/404
 * const { onError } = useQuery(SomeDocument);
 * onError(handleServerError);
 *
 * // Handle Apollo errors - shows toast for all errors
 * const { onError } = useMutation(SomeDocument);
 * onError(handleServerErrorWithToast);
 *
 * // Clear the error
 * clearError();
 * ```
 */
export function useError() {
  const toast = useToast();
  const errorStore = useErrorStore();
  const { error } = storeToRefs(errorStore);

  const showError = (options: ShowErrorOptions = {}) => {
    const statusCode = options.statusCode ?? 500;
    const config = getErrorConfig(statusCode);

    errorStore.setError({
      statusCode,
      title: options.title ?? config.title,
      description: options.description ?? config.description,
    });
  };

  /**
   * Clear the current error.
   */
  const clearError = () => {
    errorStore.clearError();
  };

  /**
   * Handle Apollo server errors by showing ErrorView for 401/403/404 status codes.
   * Other errors are ignored (use handleServerErrorWithToast for toast notifications).
   *
   * @example
   * ```typescript
   * const { onError } = useQuery(GetDataDocument);
   * onError(handleServerError);
   * ```
   */
  const handleServerError = (error: ErrorLike) => {
    const nestJsError = getFirstNestJsGraphQLError(error);

    if (nestJsError && isErrorViewStatusCode(nestJsError.extensions.statusCode)) {
      showError({
        statusCode: nestJsError.extensions.statusCode,
        description: nestJsError.message,
      });
    }
  };

  /**
   * Handle Apollo server errors by showing a toast notification.
   *
   * @param options - Optional configuration for the toast
   * @returns Error handler function
   *
   * @example
   * ```typescript
   * const { onError } = useMutation(SaveDataDocument);
   *
   * // With default title
   * onError(handleServerErrorWithToast());
   *
   * // With custom title
   * onError(handleServerErrorWithToast({ title: 'Failed to save' }));
   * ```
   */
  const handleServerErrorWithToast = (error: ErrorLike) => {
    console.error('Server error:', error);
    const nestJsError = getFirstNestJsGraphQLError(error);
    console.error('NestJS error details:', nestJsError);
    const description = nestJsError?.message ?? error.message ?? INTERNAL_ERROR.description;

    toast.add({
      title: INTERNAL_ERROR.title,
      description,
      color: 'error',
    });
  };

  /**
   * Build an error handler that dispatches by NestJS error `extensions.key`.
   * Falls back to `handleServerErrorWithToast` for unmatched keys.
   *
   * @example
   * ```typescript
   * try {
   *   await mutate();
   * } catch (error) {
   *   handleServerErrorWith({
   *     DOCUMENTS_DUPLICATE_ACTIVE: () => router.replace({ name: 'DuplicateActive' }),
   *   })(error as ErrorLike);
   * }
   * ```
   */
  const handleServerErrorWith = (handlers: Record<string, (error: ErrorLike) => void>) => (error: ErrorLike) => {
    const nestJsError = getFirstNestJsGraphQLError(error);
    const key = nestJsError?.extensions?.key as string | undefined;
    const handler = key ? handlers[key] : undefined;

    if (handler) {
      handler(error);

      return;
    }

    handleServerErrorWithToast(error);
  };

  return {
    /** Current error state (readonly reactive ref) */
    error: readonly(error),
    /** Show an error with the given options */
    showError,
    /** Clear the current error */
    clearError,
    /** Handle Apollo errors - shows ErrorView for 401/403/404 */
    handleServerError,
    /** Handle Apollo errors - shows toast notification */
    handleServerErrorWithToast,
    /** Handle Apollo errors via per-error-key handlers, falling back to toast */
    handleServerErrorWith,
  };
}
